import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

const STATUSES = ['open', 'drafted', 'done', 'dismissed'];

function formatDate(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * "Søk uten svar": help-center search gaps for the domain customer. Admins and media workers
 * see which queries came back with no articles, how often, and how many articles the latest
 * search matched. Clicking a query opens the live help-center results for it. Each open gap
 * can be drafted into an inactive help article by the AI (opened straight in the article editor
 * for review), marked as done, or dismissed.
 */
@observer
class SearchGapsDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            status: 'open',
            loading: false,
            drafting: {},
            error: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async (status = this.state.status) => {
        const { helpSearchStore } = this.props.stores;
        this.setState({ loading: true });
        await helpSearchStore.loadGaps(status);
        this.setState({ loading: false });
    }

    onClickStatus = (e) => {
        const { status } = e.target.closest('button').dataset;
        if (!STATUSES.includes(status)) {
            return;
        }
        this.setState({ status, error: null }, () => this.load(status));
    }

    openArticleDrawer = (articleId) => {
        const { drawerLevel = 1, callback } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('editArticle', {
            height: drawerHeightLarge,
            id: articleId,
            callback: () => {
                appState.toggleDrawer(false, drawerLevel + 1);
                this.load();
                if (typeof callback === 'function') {
                    callback();
                }
            },
        }, drawerLevel + 1);
    }

    onClickDraft = async (e) => {
        const id = parseInt(e.target.closest('button').dataset.id, 10);
        const { helpSearchStore } = this.props.stores;
        const { callback } = this.props;
        this.setState(prev => ({ drafting: { ...prev.drafting, [id]: true }, error: null }));
        const response = await helpSearchStore.draftArticle(id);
        this.setState(prev => ({ drafting: { ...prev.drafting, [id]: false } }));
        if (response?.status === 201 && response.data?.articleId) {
            this.openArticleDrawer(response.data.articleId);
            await this.load();
            if (typeof callback === 'function') {
                callback();
            }
            return;
        }
        this.setState({ error: response?.message || 'draft-failed' });
    }

    onClickOpenDraft = (e) => {
        const articleId = parseInt(e.target.closest('button').dataset.article, 10);
        if (articleId > 0) {
            this.openArticleDrawer(articleId);
        }
    }

    onClickQuery = (e) => {
        const id = parseInt(e.target.closest('[data-id]').dataset.id, 10);
        const { helpSearchStore, appState } = this.props.stores;
        const row = (helpSearchStore.helpSearches || []).find(r => r.id === id);
        if (!row) {
            return;
        }
        const { drawerLevel = 1, callback } = this.props;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('helpSearchResults', {
            height: drawerHeightLarge,
            row,
            callback: () => {
                this.load();
                if (typeof callback === 'function') {
                    callback();
                }
            },
        }, drawerLevel + 1);
    }

    onClickSetStatus = async (e) => {
        const { id, status } = e.target.closest('button').dataset;
        const { helpSearchStore } = this.props.stores;
        const { callback } = this.props;
        await helpSearchStore.setStatus(parseInt(id, 10), status);
        await this.load();
        if (typeof callback === 'function') {
            callback();
        }
    }

    renderRow = (row) => {
        const { drafting } = this.state;
        const isDrafting = !!drafting[row.id];
        return (
            <tr key={`help-search-gap-${row.id}`} data-id={row.id}>
                <td class='text-break'>
                    <button
                        type='button'
                        class='btn btn-link p-0 fw-semibold text-start text-decoration-none'
                        data-action='results'
                        data-id={row.id}
                        onClick={this.onClickQuery}
                        title='Vis treff i hjelpesenteret'
                    >
                        {row.queryDisplay || row.query}
                    </button>
                    {row.status === 'open' && row.lastHits > 0 && (
                        <small class='text-body-secondary'>
                            <Text id='help.search-gaps-had-hits' fields={{ count: row.lastHits }}>Last search matched {row.lastHits} articles</Text>
                        </small>
                    )}
                </td>
                <td class='text-end'>{row.zeroHitCount}</td>
                <td class='text-end'>{row.lastHits}</td>
                <td class='text-nowrap'>{formatDate(row.lastSearchedAt)}</td>
                <td class='text-end text-nowrap'>
                    {row.status === 'open' && <>
                        <button
                            type='button'
                            class='btn btn-sm btn-primary rounded-pill me-1'
                            data-action='draft'
                            data-id={row.id}
                            onClick={this.onClickDraft}
                            disabled={isDrafting}
                        >
                            {isDrafting ? <>
                                <i class='fa-solid fa-spinner fa-spin' /> <Text id='help.search-gaps-drafting'>Drafting...</Text>
                            </> : <>
                                <i class='fa-solid fa-wand-magic-sparkles' /> <Text id='help.search-gaps-draft'>Draft with AI</Text>
                            </>}
                        </button>
                        <button
                            type='button'
                            class='btn btn-sm btn-success rounded-pill me-1'
                            data-action='done'
                            data-id={row.id}
                            data-status='done'
                            onClick={this.onClickSetStatus}
                            disabled={isDrafting}
                        >
                            <i class='fa-solid fa-check' /> <Text id='help.search-gaps-done'>Done</Text>
                        </button>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-secondary rounded-pill'
                            data-action='dismiss'
                            data-id={row.id}
                            data-status='dismissed'
                            onClick={this.onClickSetStatus}
                            disabled={isDrafting}
                        >
                            <i class='fa-solid fa-eye-slash' /> <Text id='help.search-gaps-dismiss'>Hide</Text>
                        </button>
                    </>}
                    {row.status === 'drafted' && <>
                        {row.draftArticleId > 0 && (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-primary rounded-pill me-1'
                                data-action='open-draft'
                                data-article={row.draftArticleId}
                                onClick={this.onClickOpenDraft}
                            >
                                <i class='fa-solid fa-pen-to-square' /> <Text id='help.search-gaps-open-draft'>Open draft</Text>
                            </button>
                        )}
                        <button
                            type='button'
                            class='btn btn-sm btn-success rounded-pill'
                            data-action='done'
                            data-id={row.id}
                            data-status='done'
                            onClick={this.onClickSetStatus}
                        >
                            <i class='fa-solid fa-check' /> <Text id='help.search-gaps-done'>Done</Text>
                        </button>
                    </>}
                    {(row.status === 'dismissed' || row.status === 'done') && (
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-secondary rounded-pill'
                            data-action='reopen'
                            data-id={row.id}
                            data-status='open'
                            onClick={this.onClickSetStatus}
                        >
                            <i class='fa-solid fa-rotate-left' /> <Text id='help.search-gaps-reopen'>Reopen</Text>
                        </button>
                    )}
                </td>
            </tr>
        );
    }

    render() {
        const { status, loading, error } = this.state;
        const { helpSearchStore } = this.props.stores;
        const rows = helpSearchStore.helpSearches || [];

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <h5 class='ms-3 mb-1'>
                        <i class='fa-solid fa-magnifying-glass-minus me-2' />
                        <Text id='help.search-gaps-title'>Searches without an answer</Text>
                    </h5>
                    <p class='ms-3 mb-3 text-body-secondary' style='font-size: 0.9em;'>
                        <Text id='help.search-gaps-help'>Help center searches that returned no articles, most frequent first. Click a search to see its results now. Draft an article with AI, review it and publish it from the article editor, then mark the search as done. Placeholders like [FYLL INN: ...] must be filled in before publishing.</Text>
                    </p>

                    <div class='ms-3 mb-3 d-flex flex-row gap-1'>
                        {STATUSES.map(s => (
                            <button
                                key={`help-search-gaps-status-${s}`}
                                type='button'
                                class={`btn btn-sm rounded-pill ${status === s ? 'btn-primary' : 'btn-outline-secondary'}`}
                                data-status={s}
                                onClick={this.onClickStatus}
                            >
                                {s === 'open' && <Text id='help.search-gaps-status-open'>Open</Text>}
                                {s === 'drafted' && <Text id='help.search-gaps-status-drafted'>Drafted</Text>}
                                {s === 'done' && <Text id='help.search-gaps-status-done'>Done</Text>}
                                {s === 'dismissed' && <Text id='help.search-gaps-status-dismissed'>Hidden</Text>}
                            </button>
                        ))}
                        {loading && <span class='ms-2 text-body-secondary'><i class='fa-solid fa-spinner fa-spin' /></span>}
                    </div>

                    {error && (
                        <div class='alert alert-danger mx-3' role='alert'>
                            <Text id='help.search-gaps-draft-failed'>The AI could not produce a usable draft. Try again.</Text>
                            {error !== 'draft-failed' && <small class='d-block text-body-secondary'>{error}</small>}
                        </div>
                    )}

                    {rows.length === 0 && !loading && (
                        <div class='help-search-gaps-empty mx-3 p-4 text-center text-body-secondary bg-body-secondary rounded'>
                            <i class='fa-solid fa-circle-check me-1' />
                            <Text id='help.search-gaps-empty'>Nothing here — every logged search has an answer.</Text>
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div class='table-responsive mx-3'>
                            <table class='table table-sm align-middle'>
                                <thead>
                                    <tr>
                                        <th><Text id='help.search-gaps-query'>Search</Text></th>
                                        <th class='text-end'><Text id='help.search-gaps-count'>No hits</Text></th>
                                        <th class='text-end'><Text id='help.search-gaps-last-hits'>Hits</Text></th>
                                        <th><Text id='help.search-gaps-last-searched'>Last</Text></th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(this.renderRow)}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default SearchGapsDrawer;
